import type { EventInput, FrontendEvent, FrontendEventsClient } from '@appcharge/frontend-events-sdk';
import { IframeMessage } from '@/constants/iframeMessages';
import type { EventData } from '@/services/iframeMessenger';
import { deferIdle } from '@/utils/defer';
import { getEnvVariable } from '@/utils/get-env-variable';
import { toFrontendCheckoutEvent, type CheckoutEventInput } from '@/utils/frontendEventsMapper';
import { logError } from '@/utils/logger';

export interface FrontendEventsInitOptions {
  /** Checkout session token — sent as `Authorization: checkout <token>`. Required. */
  sessionToken: string;
  /** Publisher id stamped on every event (must match auth identity). */
  publisherId?: string;
  /** Override events-gateway base URL. Defaults to `VITE_EVENTS_API_URL` → `VITE_API_URL`. */
  baseUrl?: string;
}

/**
 * `iframe`-kind items still need mapping (via `toFrontendCheckoutEvent`) at
 * send-time since they carry the raw `IframeMessage` + `EventData` pair.
 * `direct`-kind items (e.g. `CHECKOUT_UI_INTERACTIONS`) have no matching
 * `IframeMessage` — the caller has already built the DTO — so they skip
 * that mapping step entirely.
 */
type QueuedEvent =
  | { kind: 'iframe'; message: IframeMessage; eventData: EventData }
  | { kind: 'direct'; event: CheckoutEventInput };

type FrontendEventsSdk = typeof import('@appcharge/frontend-events-sdk');

/** Soft cap so a stuck init cannot grow unbounded. */
const MAX_QUEUED_EVENTS = 32;

/**
 * Sentinel the SDK's `collectStandardFields` stamps when it cannot derive the
 * device from the user agent. We strip it so `device_model` is omitted from the
 * payload rather than sending a meaningless `"unknown"`.
 */
const UNKNOWN_DEVICE_MODEL = 'unknown';

/**
 * Dual-channel sink for checkout iframe SDK lifecycle events.
 *
 * Publishers still receive `postMessage` via {@link iframeMessenger}; this
 * service additionally POSTs the same lifecycle to events-gateway through
 * `@appcharge/frontend-events-sdk` with `CheckoutAuth`.
 *
 * Auth contract (hard):
 *   - Every network POST includes `Authorization: checkout <sessionToken>`
 *   - Events fired before a non-empty session token (+ publisher id) are
 *     buffered — never sent unauthenticated
 *
 * Payload contract:
 *   - Body matches `@appcharge/frontend-events` v1.3+ checkout DTOs
 *     (`CHECKOUT_LIFECYCLE` / `ORDER_AWARDING` / `PAYMENT_INTENT` /
 *     `CHECKOUT_UI_INTERACTIONS`)
 */
class FrontendEventsService {
  private client: FrontendEventsClient | null = null;
  private clientPromise: Promise<FrontendEventsClient> | null = null;
  private sdkPromise: Promise<FrontendEventsSdk> | null = null;
  private sessionToken: string | undefined;
  private publisherId: string | undefined;
  private queue: QueuedEvent[] = [];
  private baseUrlOverride: string | undefined;
  private prefetchStarted = false;

  initialize(options: FrontendEventsInitOptions): void {
    const sessionToken = normalizeRequired(options.sessionToken);
    if (!sessionToken) {
      logError('frontendEventsService.initialize requires a non-empty sessionToken', undefined, {
        hasPublisherId: Boolean(options.publisherId),
      });
      return;
    }

    this.sessionToken = sessionToken;
    if (options.publisherId) {
      const publisherId = normalizeRequired(options.publisherId);
      if (publisherId) this.publisherId = publisherId;
    }
    if (options.baseUrl) this.baseUrlOverride = options.baseUrl;

    this.client = null;
    this.clientPromise = null;

    this.prefetchSdk();
    this.flushQueue();
  }

  setPublisherId(publisherId: string): void {
    const normalized = normalizeRequired(publisherId);
    if (!normalized || normalized === this.publisherId) return;
    this.publisherId = normalized;
    this.client = null;
    this.clientPromise = null;
    this.prefetchSdk();
    this.flushQueue();
  }

  track(message: IframeMessage, eventData: EventData): void {
    const item: QueuedEvent = { kind: 'iframe', message, eventData };
    if (!this.canSendAuthenticated()) {
      this.enqueue(item);
      return;
    }

    this.scheduleSend(item);
  }

  /**
   * Sends an already-built DTO (e.g. `CHECKOUT_UI_INTERACTIONS`) with no
   * matching `IframeMessage` / publisher `postMessage`. Shares the same
   * auth/queue/keepalive-fetch pipeline as {@link track}.
   */
  trackDirect(event: CheckoutEventInput): void {
    const item: QueuedEvent = { kind: 'direct', event };
    if (!this.canSendAuthenticated()) {
      this.enqueue(item);
      return;
    }

    this.scheduleSend(item);
  }

  reset(): void {
    this.client = null;
    this.clientPromise = null;
    this.sdkPromise = null;
    this.sessionToken = undefined;
    this.publisherId = undefined;
    this.queue = [];
    this.baseUrlOverride = undefined;
    this.prefetchStarted = false;
  }

  private canSendAuthenticated(): boolean {
    return Boolean(this.sessionToken && this.publisherId);
  }

  private enqueue(item: QueuedEvent): void {
    if (this.queue.length >= MAX_QUEUED_EVENTS) {
      this.queue.shift();
    }
    this.queue.push(item);
  }

  private prefetchSdk(): void {
    if (this.prefetchStarted) return;
    this.prefetchStarted = true;
    deferIdle(() => {
      void this.loadSdk().catch((error) => {
        logError('Failed to prefetch frontend-events-sdk', error);
        this.prefetchStarted = false;
      });
    });
  }

  private loadSdk(): Promise<FrontendEventsSdk> {
    if (!this.sdkPromise) {
      this.sdkPromise = import('@appcharge/frontend-events-sdk');
    }
    return this.sdkPromise;
  }

  private resolveBaseUrl(): string {
    return (
      this.baseUrlOverride ||
      getEnvVariable('VITE_EVENTS_API_URL') ||
      getEnvVariable('VITE_API_URL', 'https://api.appcharge.com')
    );
  }

  private createKeepaliveFetch(): typeof fetch {
    const fetchImpl = typeof fetch !== 'undefined' ? fetch.bind(globalThis) : undefined;
    if (!fetchImpl) {
      throw new Error('No fetch implementation available for frontend events');
    }
    return (input: RequestInfo | URL, init?: RequestInit) => fetchImpl(input, { ...init, keepalive: true });
  }

  private async getClient(sessionToken: string, publisherId: string): Promise<FrontendEventsClient> {
    if (this.client) return this.client;
    if (this.clientPromise) return this.clientPromise;

    this.clientPromise = (async () => {
      const sdk = await this.loadSdk();
      if (this.sessionToken !== sessionToken || this.publisherId !== publisherId) {
        throw new Error('Frontend events client init aborted');
      }
      const client = new sdk.FrontendEventsClient({
        baseUrl: this.resolveBaseUrl(),
        realm: new sdk.CheckoutAuth(),
        context: {
          publisher_id: publisherId,
          session_id: sessionToken,
        },
        // FrontendEvent requires UA-derived device fields — let the SDK stamp them.
        collectStandardFields: true,
        fetch: this.createKeepaliveFetch(),
      });
      this.client = client;
      return client;
    })();

    try {
      return await this.clientPromise;
    } catch (error) {
      this.clientPromise = null;
      throw error;
    }
  }

  private flushQueue(): void {
    if (!this.canSendAuthenticated() || this.queue.length === 0) return;
    const pending = this.queue.splice(0, this.queue.length);
    for (const item of pending) {
      this.scheduleSend(item);
    }
  }

  private scheduleSend(item: QueuedEvent): void {
    if (item.kind === 'iframe' && item.message === IframeMessage.CLOSE_CHECKOUT) {
      void this.send(item);
      return;
    }
    deferIdle(() => {
      void this.send(item);
    });
  }

  private async send(item: QueuedEvent): Promise<void> {
    const sessionToken = this.sessionToken;
    const publisherId = this.publisherId;
    if (!sessionToken || !publisherId) return;

    const event = item.kind === 'direct' ? item.event : toFrontendCheckoutEvent(item.message, item.eventData);
    if (!event) return;

    const logMetadata =
      item.kind === 'direct' ? { eventType: event.event_type } : { eventType: event.event_type, message: item.message };

    try {
      const client = await this.getClient(sessionToken, publisherId);
      if (this.sessionToken !== sessionToken || !this.sessionToken) {
        logError('Skipped frontend event send: session token changed or cleared before POST', undefined, logMetadata);
        return;
      }
      const payload = await this.withResolvedDeviceModel(event);
      await client.send(payload as unknown as EventInput<FrontendEvent>, sessionToken);
    } catch (error) {
      logError('Failed to send frontend event', error, logMetadata);
    }
  }

  /**
   * Drops the SDK's `"unknown"` device_model sentinel so the field is omitted
   * from the payload. The enricher spreads caller input last, so stamping
   * `device_model: undefined` here overrides the collected `"unknown"`, and
   * `JSON.stringify` then removes the undefined key from the wire body. A
   * genuinely detected model is left untouched for the SDK to stamp.
   */
  private async withResolvedDeviceModel<T extends object>(event: T): Promise<T | (T & { device_model: undefined })> {
    const sdk = await this.loadSdk();
    const { device_model: deviceModel } = sdk.collectStandardFields();
    if (deviceModel === UNKNOWN_DEVICE_MODEL) {
      return { ...event, device_model: undefined };
    }
    return event;
  }
}

function normalizeRequired(value: string | undefined): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

export const frontendEventsService = new FrontendEventsService();

export { FrontendEventsService };
